import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button, Field, FormAction, FormGrid, SelectField } from '@crave/ui';
import { api } from '../api.ts';
import { HistoryLink } from '../components/HistoryLink.tsx';
import {
  ORDER_STATUS_WORDS,
  Section,
  Status,
  Table,
  money,
  statusWords,
  today,
} from '../components/ui.tsx';
import { useStaff } from '../lib/staff.tsx';

const STATUSES = [
  'CONFIRMED',
  'QUEUED',
  'PREPARING',
  'READY',
  'COMPLETED',
  'CANCELED',
  'NO_SHOW',
  'MANUAL_REVIEW',
  'PAYMENT_FAILED',
  'AWAITING_PAYMENT',
  'SCHEDULED',
];

// The words live with Status in components/ui.tsx; re-exported for pages that import them from here.
export { ORDER_STATUS_WORDS, statusWords };

const DATE_WORDS = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

/** "Tue, Sep 15" from a service date (YYYY-MM-DD). Anything else is returned as it is. */
export function dateWords(date: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  const d = new Date(`${date}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? date : DATE_WORDS.format(d);
}

/**
 * Narrow: the date and its "All dates" button share a row; status and search each take a full row.
 * When there is room, all four sit in one row with the button only as wide as it needs and the
 * search box wider.
 */
const FILTER_CSS = `
.crave-orders-filters { container: crave-orders-filters / inline-size; }
.crave-orders-filters > .crave-form-grid { grid-template-columns: minmax(0, 1fr) auto; }
.crave-orders-filters > .crave-form-grid > .crave-field:nth-child(n + 3) { grid-column: 1 / -1; }
/* The button shares the date's row even on phones, so it keeps the empty label line and lines up with the box. */
.crave-orders-filters > .crave-form-grid > .crave-form-action { grid-row: span 3; grid-template-rows: subgrid; margin-top: 0; }
.crave-orders-filters > .crave-form-grid > .crave-form-action > .crave-field-label { display: block; }
@container crave-orders-filters (min-width: 680px) {
  .crave-orders-filters > .crave-form-grid {
    grid-template-columns: minmax(150px, 1fr) auto minmax(160px, 1fr) minmax(220px, 1.6fr);
  }
  .crave-orders-filters > .crave-form-grid > .crave-field:nth-child(n + 3) { grid-column: auto; }
}`;

interface Row {
  id: string;
  order_number: string;
  status: string;
  pickup_label: string;
  service_date: string;
  units: number;
  amount_due_cents: number;
  customer: { name?: string; email?: string };
  review_reason: string | null;
}

export function Orders() {
  // The location's calendar day, so evening close-out doesn't open on tomorrow's (UTC) date.
  const [date, setDate] = useState<string>(today);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const staff = useStaff();
  const history = staff.can('audit.read');
  const query = useQuery({
    queryKey: ['orders', staff.location.slug, date, status, q],
    queryFn: async () =>
      (
        await api.get<{ items: Row[] }>(
          `/admin/orders?location=${staff.location.slug}&date=${date}${status ? `&status=${status}` : ''}${q ? `&q=${encodeURIComponent(q)}` : ''}`,
        )
      ).data.items,
    refetchInterval: 15_000,
  });
  return (
    <Section title="Orders">
      <style>{FILTER_CSS}</style>
      <div className="crave-orders-filters">
        <FormGrid min={200}>
          <Field
            label="Service date"
            type="date"
            value={date === 'all' ? '' : date}
            onChange={(e) => setDate(e.target.value || 'all')}
          />
          <FormAction>
            <Button type="button" variant="secondary" onClick={() => setDate('all')}>
              All dates
            </Button>
          </FormAction>
          <SelectField
            label="Status"
            placeholder="Any status"
            options={STATUSES.map((s) => [s, statusWords(s)] as const)}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          />
          <Field
            label="Search"
            placeholder="Search name, email, number"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </FormGrid>
      </div>
      <Table
        head={['Order', 'Status', 'Pickup', 'Customer', 'Pizzas', 'Due', ...(history ? [''] : [])]}
        rowKeys={(query.data ?? []).map((o) => o.id)}
        rows={(query.data ?? []).map((o) => [
          <Link key="n" to={`/orders/${o.id}`}>
            {o.order_number}
          </Link>,
          <>
            <Status s={o.status} />
            {o.review_reason ? (
              <div style={{ fontSize: 12, color: 'var(--crave-red)' }}>{o.review_reason}</div>
            ) : null}
          </>,
          `${dateWords(o.service_date)}, ${o.pickup_label}`,
          <>
            {o.customer?.name ?? <span style={{ color: 'var(--ink-soft)' }}>No name given</span>}
            <div style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{o.customer?.email}</div>
          </>,
          o.units,
          money(o.amount_due_cents),
          ...(history
            ? [
                <HistoryLink
                  key="h"
                  entityType="orders"
                  entityId={o.id}
                  name={`order ${o.order_number}`}
                  style={{ minHeight: 0 }}
                />,
              ]
            : []),
        ])}
        empty={query.isLoading ? 'Loading…' : 'No orders match.'}
      />
    </Section>
  );
}
